import { getPurchasedGoodsFoodListQueryKey } from '@shared/api/lib/purchased-goods/purchased-goods';
import { getSoldGoodsFoodListQueryKey } from '@shared/api/lib/sold-goods/sold-goods';
import { OperationReportTypeEnum } from '@shared/api/types';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

/**
 * Structural shape shared by the purchased and sold food list responses — only
 * the fields this optimistic update touches. numberOfItemsToReview rides on the
 * response but isn't in the generated type.
 */
type FoodListData = {
  results?: { uuid: string }[];
  count?: number;
  numberOfItemsToReview?: number;
};

/**
 * Optimistically drop resolved rows from every cached food list query (purchased
 * or sold, per `reportType`) and decrement the review counts. Used after a bulk
 * action enqueues — on the Review tab the rows leave immediately and the review
 * badge drops, without waiting for the async job (an immediate refetch could
 * re-surface them). On the main Food and drink table the rows reappear once the
 * list refetches. Defaults to purchase so existing purchased callers are
 * unchanged.
 */
export function useOptimisticFoodResolution(
  reportType: OperationReportTypeEnum = OperationReportTypeEnum.purchase
) {
  const queryClient = useQueryClient();

  return useCallback(
    (resolvedUuids: string[]) => {
      const listQueryKey =
        reportType === OperationReportTypeEnum.sales
          ? getSoldGoodsFoodListQueryKey()
          : getPurchasedGoodsFoodListQueryKey();
      const removed = new Set(resolvedUuids);
      queryClient.setQueriesData<FoodListData>(listQueryKey, (prev) => {
        if (!prev) return prev;
        const results = (prev.results ?? []).filter(
          (row) => !removed.has(row.uuid)
        );
        const dropped = (prev.results?.length ?? 0) - results.length;
        if (!dropped) return prev;
        return {
          ...prev,
          results,
          count: Math.max(0, (prev.count ?? 0) - dropped),
          numberOfItemsToReview: Math.max(
            0,
            (prev.numberOfItemsToReview ?? 0) - dropped
          ),
        };
      });
    },
    [queryClient, reportType]
  );
}
